import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { ContentContainer } from '@sorare/core/src/components/landing/LandingMultiSport/ui';

const StyledContainer = styled(ContentContainer)`
  & p {
    margin-bottom: 1em;
    margin-top: 0.5em;
    line-height: 1.5em;
    color: var(--c-nd-400-opaque);
  }
  & ul li {
    list-style: disc;
    color: var(--c-nd-400-opaque);
  }
`;

const messages = defineMessages({
  content: {
    id: 'Football.FAQ.content',
    defaultMessage: `

## What is Sorare?

Welcome to the thrilling world of fantasy football (i.e. “soccer”), a fan experience where your football knowledge and managerial skills come to life! At Sorare, we're more than just a fantasy platform; we're a community of football enthusiasts who live, breathe, and dream about the sport.

Sorare offers football fans an extraordinary way to engage with the
world’s top leagues and players, blending the excitement of the sport
with the strategic elements of lineup building and team management. How
so? With Sorare, you own and build your dream fantasy team with
officially licensed digital player cards – buying, selling, trading, and
competing with them – to win rewards like cash, tickets, and VIP
experiences.

Sorare has nearly 4 million users and exclusive partnerships with the NBA, MLB, and world’s top club football leagues, and teams, including the [Premier League](https://sorare.com/football/premier-league), [Bundesliga](https://sorare.com/football/bundesliga), [LALIGA](https://sorare.com/football/laliga), and [Serie A](https://sorare.com/football/serie-a). Whether you're a seasoned fantasy football manager or a newcomer eager to dive into the world of fantasy football, Sorare is your ultimate destination.

## What is fantasy football?

Fantasy football is an interactive online game that transforms you into
the manager of your own team – and in Sorare Football, into a real
owner, as you own your digital player cards used in Sorare fantasy
competitions. Fantasy football has grown from a hobby among friends to a
global phenomenon, uniting millions of football fans.

In fantasy football, your objective is simple yet challenging: assemble
a team of real-life football players and compete against friends and
rivals for bragging rights and, in the case of Sorare, amazing rewards.
As these players perform in actual matches, your fantasy team earns
points based on their on-field actions like goals, assists, clean
sheets, and more. It's a test of your football knowledge, strategic
planning, and adaptability.

## Why Sorare is your go-to for fantasy football game

Sorare takes fantasy football to the next level. Unlike traditional
fantasy sports platforms, we offer a unique twist - a platform where you
not only build your fantasy team but also collect and trade officially
licensed digital cards of players. This feature adds an extra layer of
strategy and excitement, allowing you to truly own a part of the game.
With Sorare, you're not just a manager; you're a collector and trader,
engaging with the game in a completely new dimension.

## How to play fantasy football with Sorare

It’s free and easy to starting your fantasy football journey with
Sorare:

  - Sign up for free and build a team of players using cards you own
  - Set a lineup and enter a competition (join anytime, competitions are twice-weekly)
  - Track your team’s score as you earn points and rewards based on real-life performances
  - Boost your team by buying, selling, and trading player cards on Sorare’s Marketplace

### Building your dream team

Assembling your dream team is where your skills as a manager are truly
tested. Pay attention to player statistics, such as goals, assists, and
defensive records. But don't just look at numbers; consider the player's
form, injury history, and upcoming fixtures. Balancing star players with
underrated talents can often be the key to a well-rounded, successful
team.

### Navigating the football season

The football season is a marathon, not a sprint. As the season
progresses, stay updated on game weeks, fixtures, and player transfers.
These factors can significantly impact player performances and,
consequently, your team's success. Adapting your team to the season's
dynamics is crucial - be prepared to make timely transfers and tweak
your strategy based on the latest developments.

### Earning cash prizes in fantasy football

One of the exhilarating aspects of fantasy football with Sorare is the
opportunity to earn cash prizes. By topping leaderboards and
outperforming your competitors, you can earn rewards that add a tangible
excitement to your fantasy football experience. Your acumen in player
selection, team management, and strategic foresight are what will set
you apart in the quest for these prizes.

### Tips to maximize your winnings

To maximize your potential for winnings, stay abreast of the latest in
football. Regularly check player stats, team news, and match analyses.
Watching matches can give you insights beyond what statistics can
provide - understanding player roles, team strategies, and even
potential breakthrough talents. Another tip is to learn from top fantasy
managers. Study their teams, understand their strategies, and see how
they adapt to different situations.

### Managing your fantasy football team

Your journey in fantasy football is personalized at Sorare. You can
choose a unique name for your team, reflecting your style or footballing
philosophy. Engaging with Sorare's community is also beneficial; you can
exchange tips, discuss strategies, and gain insights from other fantasy
managers – as well as buy, sell, and trade with fellow community
members/managers on Sorare’s marketplace. Remember, every decision, from
your team name to player transfers, shapes your path in the world of
fantasy football.

### Getting insight from the best

One of the best ways to enhance your fantasy football skills is by
observing the best in the game. Look at top fantasy teams, analyze their
composition, and understand the rationale behind their player choices.
How do they react to unexpected player performances or last-minute
injuries? Learning from these insights can significantly improve your
decision-making and strategic planning.`,
  },
});

export const FAQ = () => {
  const { formatMessage } = useIntl();
  const content = formatMessage(messages.content);

  return (
    <StyledContainer>
      <LazyMarkdown data={content} />
    </StyledContainer>
  );
};
